import type { MachineId, SimEvent } from '../shared/contract.js';
import { ctxFor, type Pipeline, type StageId } from './modifier.js';
import { MATERIAL_MULT, type TokenRuntime } from './token.js';

/**
 * THE PAYOUT PIPELINE — an ordered, spliceable stage list.
 *
 * economy.md: base -> material -> STRÄHNE -> TOPF -> floor -> coins + tickets.
 * Rewiring items literally reorder this array (Umgekehrte Buchführung swaps material
 * and STRÄHNE), which is why "where does big scaling come from" has an answer that is
 * not "+X %". The resolved order is hashed into the replay, so an order bug is a
 * determinism bug and the twin-run test catches it.
 */

export const DEFAULT_STAGE_ORDER: readonly StageId[] = ['base', 'material', 'straehne', 'topf', 'floor'];

export interface PayoutInput {
  token: TokenRuntime | null;
  machine: MachineId;
  tick: number;
  /** token face × pocket/match value */
  base: number;
  jackpot: boolean;
  straehne: number;
  /** current pot; a jackpot drains it into the payout */
  topf: number;
  floorMult: number;
  /** default share of the result paid as tickets rather than coins */
  ticketRatio: number;
}

export interface PayoutResult {
  muenzen: number;
  tickets: number;
  /** how much of DER TOPF this payout drained */
  topfDrain: number;
  order: StageId[];
  stages: { stage: StageId; before: number; after: number }[];
  total: number;
}

export function runPayout(pipe: Pipeline, input: PayoutInput, emit: (e: SimEvent) => void): PayoutResult {
  const base = ctxFor(input.machine, input.tick, input.token);
  const order = pipe.query('stageOrder', base, [...DEFAULT_STAGE_ORDER]);

  let v = 0;
  let topfDrain = 0;
  const stages: PayoutResult['stages'] = [];

  for (const stage of order) {
    const before = v;
    switch (stage) {
      case 'base':
        v = input.base;
        break;
      case 'material': {
        const matBase = input.token ? MATERIAL_MULT[input.token.material] : 1;
        const mult = pipe.query('materialMult', ctxFor(input.machine, input.tick, input.token), matBase);
        v = v * mult;
        break;
      }
      case 'straehne':
        v = v * input.straehne;
        break;
      case 'topf':
        if (input.jackpot) {
          topfDrain = input.topf;
          v = v + topfDrain;
        }
        break;
      case 'floor':
        v = v * input.floorMult;
        break;
    }
    // "tap a stage": a skim, a fork, an overflow valve. The stage has computed its value;
    // anyone who wants a hand in it gets it here, in resolution order, on the record.
    v = pipe.query('stageValue', { ...base, stage }, v);
    stages.push({ stage, before, after: v });
    // contract v1: name the machine. With Der Deckel and cross-machine payout chains,
    // "the active cabinet" is not a safe assumption for the renderer.
    emit({ t: 'payoutStage', stage, before, after: v, machine: input.machine });
  }

  const ratio = clamp01(
    pipe.query('ticketRatio', ctxFor(input.machine, input.tick, input.token), input.ticketRatio),
  );
  const total = v;
  const tickets = Math.floor(total * ratio);
  const muenzen = Math.floor(total - total * ratio);

  return { muenzen, tickets, topfDrain, order, stages, total };
}

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Stage order as a stable string — goes into the world hash. */
export function orderKey(order: readonly StageId[]): string {
  return order.join('>');
}
